import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@mui/material';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, rectSortingStrategy, arrayMove, useSortable, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { dataFetcher, endpoints } from 'Api';
import { uploadCustomBanner } from 'Utils/uploadCustomBanner';
import { markSectionExplored, EXPLORED_SECTION } from 'Utils/onboardingChecklist';
import { AnimatedFilePenLine } from 'Components/reactbits/AnimatedIcons';
import { loadRowContent, contentReorderType } from './contentLoaders';
import { trackSave } from './saveBus';
import styles from './CategoryEditModal.module.css';

// One sortable content tile (card-less: thumbnail + name, like the layout/1 grid).
const SortTile = ({ item }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 5 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} className={styles.tile} {...attributes} {...listeners}>
      <span className={styles.thumb} style={{ backgroundImage: `url(${item.image})` }} />
      {item.text ? <span className={styles.caption} title={item.text}>{item.text}</span> : null}
    </div>
  );
};

// In-place edit for a category / playlist / album: name (inline on the banner
// for categories), cover image, and a card-less 5-per-row reorder grid.
const CategoryEditModal = ({
  open, item, section, userId, onClose, onSaved,
}) => {
  const isCategory = section?.reorderType === 'category';
  const [name, setName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [contents, setContents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [savingName, setSavingName] = useState(false);
  // "Change background": swaps the content grid for a background picker whose
  // first tile is the custom upload and the rest are the preset backgrounds.
  const [bgMode, setBgMode] = useState(false);
  const [bgPresets, setBgPresets] = useState(null);
  const contentsRef = useRef([]);
  contentsRef.current = contents || [];
  const fileRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!open || !item) return undefined;
    setName(item.title || '');
    setImageUrl(item.imagekit_icon || item.icon || '');
    setEditingName(false);
    setBgMode(false);
    let alive = true;
    setLoading(true);
    loadRowContent({
      reorderType: section.reorderType, itemId: item.id, userId, limit: 60,
    })
      .then((list) => { if (alive) setContents(list); })
      .catch(() => { if (alive) setContents([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  // Persist name (+ image for categories) via the addEdit* endpoint.
  const persistMeta = async (nextName, nextImage) => {
    let endpoint;
    let payload;
    if (section.reorderType === 'category') {
      endpoint = endpoints.addEditCategory;
      payload = { title: nextName, category_id: item.id, icon: nextImage };
    } else if (section.reorderType === 'playlist') {
      endpoint = endpoints.addEditPlaylist;
      payload = { title: nextName, playlist_id: item.id };
    } else {
      endpoint = endpoints.addEditAlbum;
      payload = { title: nextName, album_id: item.id, no_order_change: true };
    }
    const p = dataFetcher(endpoint, payload);
    trackSave(p);
    await p;
    if (onSaved) onSaved({ id: item.id, title: nextName, icon: nextImage });
  };

  const startEditName = () => { setDraftName(name); setEditingName(true); };
  const cancelEditName = () => setEditingName(false);
  const saveEditName = async () => {
    const t = (draftName || '').trim();
    if (!t) { setEditingName(false); return; }
    setSavingName(true);
    try { await persistMeta(t, imageUrl); setName(t); } catch (e) { /* noop */ } finally {
      setSavingName(false);
      setEditingName(false);
    }
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const url = await uploadCustomBanner(file);
      if (url) { setImageUrl(url); await persistMeta(name, url); }
    } catch (err) { /* noop */ } finally { setUploading(false); }
  };

  // Load the preset background images once the picker is opened.
  useEffect(() => {
    if (!bgMode || bgPresets) return undefined;
    let alive = true;
    dataFetcher(endpoints.getBackgroundImages)
      .then((res) => {
        if (!alive) return;
        const list = (res?.data || []).filter((it) => parseInt(it.is_default, 10) === 0);
        setBgPresets(list);
      })
      .catch(() => { if (alive) setBgPresets([]); });
    return () => { alive = false; };
  }, [bgMode, bgPresets]);

  // Pick a preset (or the current custom) as the category background.
  const selectBg = async (url) => {
    if (!url || url === imageUrl) return;
    setImageUrl(url);
    try { await persistMeta(name, url); } catch (e) { /* noop */ }
  };

  const persistContentOrder = (movedId, list, prevList) => {
    // Ticks "Rearrange" off the dashboard getting-started checklist.
    markSectionExplored(EXPLORED_SECTION.rearrange);
    const pos = list.findIndex((c) => c.id === movedId);
    const afterId = pos > 0 ? list[pos - 1]?.id : null;
    trackSave(dataFetcher(endpoints.reorderItem, {
      type: contentReorderType(section.reorderType),
      item_id: movedId,
      parent_id: item.id,
      ...(afterId ? { after_id: afterId } : {}),
    }).catch(() => {}), {
      title: name || item.title, items: list, prevItems: prevList, movedId,
    });
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const list = contentsRef.current;
    const from = list.findIndex((c) => c.id === active.id);
    const to = list.findIndex((c) => c.id === over.id);
    if (from < 0 || to < 0) return;
    const next = arrayMove(list, from, to);
    setContents(next);
    persistContentOrder(active.id, next, list);
  };

  const contentNoun = section?.reorderType === 'album' ? 'Photos' : 'Videos';
  const count = contents ? contents.length : (item?.video_count ?? item?.photo_count ?? '');

  const nameEditRow = (
    <div className={styles.nameEditRow}>
      <input
        className={styles.nameInput}
        value={draftName}
        autoFocus
        disabled={savingName}
        onChange={(e) => setDraftName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') saveEditName();
          if (e.key === 'Escape') cancelEditName();
        }}
        placeholder={`${section?.singular || 'Item'} name`}
      />
      <button type="button" className={styles.nameSave} onMouseDown={(e) => e.preventDefault()} onClick={saveEditName} disabled={savingName}>Save</button>
      <button type="button" className={styles.nameCancel} onMouseDown={(e) => e.preventDefault()} onClick={cancelEditName} disabled={savingName}>Cancel</button>
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5, backgroundImage: 'none' } }}>
      <div className={styles.wrap}>
        <div className={styles.head}>
          <h3 className={styles.title}>{`Edit ${section?.singular || 'item'}`}</h3>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <CloseRoundedIcon fontSize="small" />
          </button>
        </div>

        {isCategory ? (
          <div
            className={styles.banner}
            style={imageUrl ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.6)), url(${imageUrl})` } : undefined}
          >
            <div className={styles.bannerName}>
              {editingName ? nameEditRow : (
                <button type="button" className={styles.nameDisplay} onClick={startEditName} title="Edit name">
                  <span className={styles.nameText}>{name || 'Untitled'}</span>
                  <AnimatedFilePenLine size={17} />
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickImage} />
            <button type="button" className={styles.coverBtn} onClick={() => setBgMode((m) => !m)} disabled={uploading}>
              {uploading ? 'Uploading…' : (bgMode ? 'Done' : 'Change background')}
            </button>
          </div>
        ) : (
          <div className={styles.form}>
            <span className={styles.label}>Name</span>
            {editingName ? nameEditRow : (
              <button type="button" className={styles.nameDisplayLight} onClick={startEditName} title="Edit name">
                <span className={styles.nameText}>{name || 'Untitled'}</span>
                <AnimatedFilePenLine size={15} />
              </button>
            )}
          </div>
        )}

        <div className={styles.contentHead}>
          <span className={styles.contentTitle}>
            {bgMode ? 'Background' : contentNoun}
            {!bgMode && count !== '' && count != null ? <span className={styles.contentCount}>{count}</span> : null}
          </span>
          <span className={styles.contentHint}>
            {bgMode ? 'Pick a background · first tile is a custom upload' : 'Drag to reorder · auto-saved'}
          </span>
        </div>

        {loading && !bgMode ? (
          <div className={styles.gridMsg}>Loading…</div>
        ) : bgMode ? (
          <div className={styles.gridScroll}>
            <div className={styles.grid}>
              {/* First object: custom upload. */}
              <div
                className={`${styles.tile} ${styles.pickTile}`}
                role="button"
                tabIndex={0}
                onClick={() => fileRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter') fileRef.current?.click(); }}
              >
                <span className={`${styles.thumb} ${styles.customThumb}`} aria-hidden>
                  <AddRoundedIcon fontSize="inherit" />
                </span>
                <span className={styles.caption}>{uploading ? 'Uploading…' : 'Custom'}</span>
              </div>
              {/* Preset background thumbnails. */}
              {(bgPresets || []).map((p) => {
                const url = p.imagekit_image || p.image;
                return (
                  <div
                    key={p.id}
                    className={`${styles.tile} ${styles.pickTile} ${imageUrl === url ? styles.pickActive : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectBg(url)}
                    onKeyDown={(e) => { if (e.key === 'Enter') selectBg(url); }}
                  >
                    <span className={styles.thumb} style={{ backgroundImage: `url(${url})` }} />
                  </div>
                );
              })}
              {bgPresets === null && <div className={styles.gridMsg}>Loading presets…</div>}
            </div>
          </div>
        ) : !contents || contents.length === 0 ? (
          <div className={styles.gridMsg}>Nothing here yet</div>
        ) : (
          <div className={styles.gridScroll}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={contents.map((c) => c.id)} strategy={rectSortingStrategy}>
                <div className={styles.grid}>
                  {contents.map((c) => (<SortTile key={c.id} item={c} />))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default CategoryEditModal;
